import { MutableRefObject, useCallback, useRef, useState } from "react";

export interface UseDisclosureProps {
  isOpen: boolean;
  isOpenRef: MutableRefObject<boolean>;
  onClose: () => void;
  onOpen: () => void;
  onToggle: () => void;
  disclosureProps: {
    open: boolean;
    onClose: () => void;
  };
  buttonProps: {
    onClick: () => void;
  };
}

/**
 * Custom hook to control feedback components providing open, close, and toggle methods.
 *
 * Based on https://chakra-ui.com/docs/hooks/use-disclosure
 */
export const useDisclosure = (defaultIsOpen = false): UseDisclosureProps => {
  const [isOpen, setIsOpen] = useState(defaultIsOpen);
  const isOpenRef = useRef(isOpen);

  const onClose = useCallback(() => {
    isOpenRef.current = false;
    setIsOpen(false);
  }, []);

  const onOpen = useCallback(() => {
    isOpenRef.current = true;
    setIsOpen(true);
  }, []);

  const onToggle = useCallback(() => {
    isOpenRef.current = !isOpenRef.current;
    setIsOpen((prev) => !prev);
  }, []);

  const disclosureProps = {
    open: isOpen,
    onClose,
  };

  const buttonProps = {
    onClick: onToggle,
  };

  return {
    isOpen,
    isOpenRef,
    onClose,
    onOpen,
    onToggle,
    disclosureProps,
    buttonProps,
  };
};

export default useDisclosure;
